import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Sparkles, Clock, Mail, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface NewsletterSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

export const NewsletterSuccessModal = ({ isOpen, onClose, userEmail }: NewsletterSuccessModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { t } = useTranslation();
  
  const steps = t('newsletterSuccess.steps', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;
  
  const stepIcons = [
    <CheckCircle className="w-12 h-12 text-newsletter-primary" />,
    <Sparkles className="w-12 h-12 text-newsletter-accent" />,
    <Mail className="w-12 h-12 text-newsletter-secondary" />
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            {stepIcons[currentStep]}
          </div>
          
          <DialogTitle className="text-2xl font-semibold bg-gradient-to-r from-newsletter-primary to-newsletter-accent bg-clip-text text-transparent mb-2 text-center">
            {steps[currentStep].title}
          </DialogTitle>
          
          <DialogDescription className="text-base text-center">
            {steps[currentStep].description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Step indicators */}
          <div className="flex justify-center gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep
                    ? 'bg-newsletter-primary'
                    : index < currentStep
                    ? 'bg-newsletter-primary/50'
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="text-center">
                <Badge variant="secondary" className="bg-newsletter-primary/10 text-newsletter-primary border-newsletter-primary/20">
                  <Clock className="w-3 h-3 mr-1" />
                  {t('newsletterSuccess.trialBadge')}
                </Badge>
              </div>
              <div className="bg-newsletter-gradient/10 rounded-lg p-4 border border-newsletter-primary/20">
                <p className="text-sm text-center">
                  <strong>{t('newsletterSuccess.emailRegistered')}</strong> {userEmail}
                </p>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-3">
                {(t('newsletterSuccess.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                  <div key={index} className="flex items-center justify-center gap-3">
                    <div className="w-2 h-2 bg-newsletter-primary rounded-full"></div>
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  {t('newsletterSuccess.afterTrial')}
                </p>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="bg-newsletter-primary/5 rounded-lg p-4 border border-newsletter-primary/20">
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="w-4 h-4 text-newsletter-primary" />
                    <span className="font-semibold text-sm text-center">{t('newsletterSuccess.nextSteps.title')}</span>
                  </div>
                  <ul className="text-sm space-y-2 text-center">
                    {(t('newsletterSuccess.nextSteps.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between gap-3">
            {currentStep > 0 && (
              <Button variant="ghost" onClick={prevStep} className="flex-1">
                {t('newsletterSuccess.navigation.previous')}
              </Button>
            )}
            
            {currentStep < steps.length - 1 ? (
              <Button onClick={nextStep} className="flex-1 bg-newsletter-primary hover:bg-newsletter-primary/90">
                {t('newsletterSuccess.navigation.next')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={onClose} className="flex-1 bg-newsletter-primary hover:bg-newsletter-primary/90">
                {t('newsletterSuccess.navigation.close')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};